import type { ReactElement } from 'react';
import type { LoggedInUserMatch } from '@kirin/api-client/contracts/shadow-ai';

/**
 * Tailwind text color for a LoggedInUser email by its match to the Kirin user
 * (KIR-2916): matched → green, corporate → amber, personal → red, unknown →
 * muted gray.
 */
function loggedInUserTextClass(match: LoggedInUserMatch): string {
  switch (match) {
    case 'matched':
      return 'text-green-600 dark:text-green-400';
    case 'corporate':
      return 'text-amber-600 dark:text-amber-400';
    case 'personal':
      return 'text-red-600 dark:text-red-400';
    case 'unknown':
    default:
      return 'text-muted-foreground';
  }
}

/**
 * Renders a single logged-in account email colored by its match, or a gray
 * "Unknown" when no account was extracted. Used wherever a single value (not
 * aggregate chips) is shown — the expanded per-tool users table and the
 * Running-Now per-process row.
 */
export function LoggedInUserText({
  email,
  match,
}: {
  email?: string | null;
  match?: LoggedInUserMatch;
}): ReactElement {
  if (!email) {
    return <span className="text-muted-foreground">Unknown</span>;
  }
  return (
    <span className={loggedInUserTextClass(match ?? 'unknown')}>{email}</span>
  );
}

LoggedInUserText.displayName = 'LoggedInUserText';
